import Hero from "@/components/Hero";
import About from "@/components/About";
import Projects from "@/components/Projects";
import Skills from "@/components/Skills";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import PageTransition from "@/components/PageTransition";
import { useEffect } from "react";
import { siteConfig } from "@/config";

const Index = () => {
  // Update document title and generate structured data dynamically
  useEffect(() => {
    // Set document title
    document.title = siteConfig.title;

    // Add structured data for the home page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      name: siteConfig.name,
      description: siteConfig.description,
      mainEntity: {
        "@type": "Person",
        name: siteConfig.name,
        description: siteConfig.about.description,
        jobTitle: siteConfig.hero.subtitle,
        url: siteConfig.url,
      },
    };

    // Add the structured data script
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <PageTransition>
      <div className="bg-black text-white noise-bg">
        <header className="relative">
          <Navbar />
          {siteConfig.hero && <Hero />}
        </header>

        <main id="main-content">
          {siteConfig.about.showSection && (
            <section id="about" aria-labelledby="about-heading">
              <About />
            </section>
          )}

          {siteConfig.skills.showSection && (
            <section id="skills" aria-labelledby="skills-heading">
              <Skills />
            </section>
          )}

          {siteConfig.projects.showSection && (
            <section id="projects" aria-labelledby="projects-heading">
              <Projects />
            </section>
          )}

          {siteConfig.resume.showSection && (
            <section id="resume" className="py-20 px-6 container mx-auto">
              <div className="max-w-4xl mx-auto text-center">
                <h2
                  className="text-3xl md:text-4xl font-bold mb-6 opacity-0 animate-fade-in"
                  id="resume-heading"
                  style={{ animationDelay: "200ms" }}
                >
                  {siteConfig.resume.title}
                </h2>
                <p
                  className="text-neutral-300 mb-10 max-w-2xl mx-auto opacity-0 animate-fade-in"
                  style={{ animationDelay: "300ms" }}
                >
                  {siteConfig.resume.description}
                </p>
                <div
                  className="relative opacity-0 animate-fade-in"
                  style={{ animationDelay: "400ms" }}
                >
                  <a
                    href={siteConfig.resume.filePath}
                    download
                    className="inline-block bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-neutral-200 transition-all duration-500 relative overflow-hidden group z-10 transform hover:scale-105"
                    aria-label="Download resume"
                  >
                    <span className="relative z-10">
                      {siteConfig.resume.buttonText}
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-white to-neutral-200 transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500"></span>
                  </a>
                  {/* Add decorative elements */}
                  <div
                    className="absolute -top-5 -left-5 w-10 h-10 border-t border-l border-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ animationDelay: "500ms" }}
                  ></div>
                  <div
                    className="absolute -bottom-5 -right-5 w-10 h-10 border-b border-r border-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ animationDelay: "600ms" }}
                  ></div>
                </div>
              </div>
            </section>
          )}

          {siteConfig.contact.showSection && (
            <section id="contact" aria-labelledby="contact-heading">
              <Contact />
            </section>
          )}
        </main>

        <footer>
          <Footer />
        </footer>
      </div>
    </PageTransition>
  );
};

export default Index;
